/**
 * WHAT THE GAME'S DRIVERS SAY. D-EZ-71b.
 *
 * Howard, 20 Aug 2026, after the model-written quotes went in:
 * *"Ok you can make a large library for the people not using an api."*
 *
 * ===========================================================================
 * TWO SOURCES OF WORDS, AND THEY DO NOT MEET
 * ===========================================================================
 *
 * With a key, the writer composes the quote in the same completion as the
 * article and nothing is stored — his instruction, and the gate in
 * `worker/paper.ts` holds it to the same rules this file holds itself to.
 *
 * Without one, the words come from here. That is not a degraded path: most
 * players will never paste a key, so for most people this IS what the drivers
 * sound like, permanently and by design — the same argument `fallbackPaper`
 * has always made about the article around it.
 *
 * ===========================================================================
 * THE RULES THESE LINES OBEY, AND WHY THEY ARE TESTED RATHER THAN TRUSTED
 * ===========================================================================
 *
 * ⚠ HOW IT FELT, NEVER WHAT HAPPENED. Not one line states a result, a position,
 * a lap, a margin, a count or a sum of money. Those are the ledger's to state
 * and the article states them; a quote that also stated them could contradict
 * the paragraph above it, and a paper that disagrees with itself is worse than
 * one with no quotes in it.
 *
 * ⚠ AND NOT ANOTHER MAN'S NAME. A quote naming somebody makes a claim about a
 * person the interview did not involve. "He knows what he did" is a feeling;
 * "Hargrove knows what he did" is an accusation the ledger cannot support.
 *
 * Both are enforced in `quoteBank.test.ts` against every line in the file, for
 * the same reason the no-quotes rule was enforced rather than requested: a rule
 * that lives only in a comment is a wish. The digit check in particular is
 * character-for-character the one the Worker applies to the model, so a line
 * added here that a model would have been refused for is refused here too.
 *
 * ===========================================================================
 * WHY IT IS THIS BIG
 * ===========================================================================
 *
 * A season runs thirty-six races and the chart fires on most of them, so a bank
 * of six lines would have a man saying the same thing twice by June. The
 * selection also mixes the WEEK into the key, so a driver interviewed twice in a
 * year says two different things — without that, one man reads as a recording.
 */

import type { PaperFacts, PaperInterview, InterviewBand } from './paperFacts.ts';

export interface BankQuote {
  speaker: string;
  /** His words, with no quotation marks — the paper adds those. */
  text: string;
  /** How the paper introduces him. */
  attribution: string;
}

/**
 * ⚠ THE KEYS ARE THE CHART'S SIX CATEGORIES, PLUS TWO REFINEMENTS.
 *
 * A man who has just won his first does not sound like a man collecting his
 * fourth of the year, and a man who ran well in the pack does not sound like one
 * who spent the day in traffic. Those two are worth splitting; the rest are not,
 * because the category already says what he is talking about.
 */
type Bucket = InterviewBand | 'winner-first' | 'top-or-middle-good';

const BANK: Readonly<Record<Bucket, readonly string[]>> = Object.freeze({
  'winner-first': Object.freeze([
    'I have been coming here a long time hoping for one of these.',
    'You wonder if it is ever going to happen and then it does.',
    'It has not sunk in and I do not expect it to tonight.',
    'Every man on this crew has been waiting on a day like this longer than I have.',
    'I have dreamed about this since I was old enough to hold a wheel.',
    'My daddy is somewhere up in that grandstand and I hope he saw it.',
    'I kept waiting for somebody to come and take it away from me.',
    'That is what all the winters in a cold shop were for.',
    'I did not let myself believe it until I saw the flag.',
    'You can go a whole career and never get one. I have got one.',
    'I have come close enough times to know exactly what this is worth.',
    'Nobody gave us anything today and that makes it better.',
    'I would like to thank every man who ever bolted a fender on for me.',
    'It feels like somebody took a weight off my chest.',
    'I have been told I could do this. It is different to know it.',
    'This one belongs to a lot of people who never get their name in the paper.',
  ]),
  winner: Object.freeze([
    'The car was under me all afternoon and I tried not to get in its way.',
    'You do not get many of these. I am going to sit with this one a while.',
    'I kept telling myself to be patient and for once I listened.',
    'We unloaded good and the crew never let it get away from us.',
    'It is a hard place to win and it does not get easier.',
    'I could feel it coming to me the longer the day went.',
    'The men over the wall won this one, not me.',
    'I have run bad enough here to enjoy this properly.',
    'That is the way you draw it up and it almost never goes that way.',
    'I never had to use everything I had, and that is a good feeling.',
    'We have been close for a while. It was going to fall one day.',
    'You take them however they come and this one came easy.',
    'I was more worried at the end than I was at the start.',
    'The whole shop earned that one on Wednesday, not today.',
    'I would rather be lucky than good, and today I was both.',
    'It is a long way home when you run bad. Tonight it will be short.',
  ]),
  wrecked: Object.freeze([
    'I am fine. The car is not.',
    'That is racing. It does not make it any easier to walk back.',
    'I never saw it. By the time I did there was nowhere to go.',
    'We had something today and we do not get to find out what.',
    'I am sore and I am mad and I will be over one of those by morning.',
    'You hate to load it up early. This team deserved better than that.',
    'There was smoke and then there was wall.',
    'I got hit in the left rear and after that I was a passenger.',
    'The boys will be up all night and it was not their doing.',
    'Everybody walked away, so it was a good wreck as they go.',
    'I do not remember much between the hit and the ambulance ride.',
    'You cannot see a thing in the middle of that.',
    'It is a shame. That car was the best one we have built.',
    'I will feel this one in the morning and again in the truck.',
    'We were where we needed to be and it did not matter.',
    'There is nothing to say. You go home and build another one.',
  ]),
  dnf: Object.freeze([
    'It let go and there was nothing anybody could have done about it.',
    'We will take it apart this week and find out what it was.',
    'That is a hard way to spend a Sunday.',
    'The boys built me a good car. It just did not want to go the distance.',
    'You can drive around a lot of things. You cannot drive around that.',
    'I felt it going for a while and I was hoping I was wrong.',
    'It started to tighten up and then it was done.',
    'I hate it for the crew more than for me.',
    'We will be better for knowing what broke.',
    'You do everything right all week and it lasts an hour.',
    'It is nobody in this garage that let me down.',
    'That is the part of this business nobody buys a ticket to see.',
    'I would rather be beat than parked.',
    'We will make it right before we come back here.',
  ]),
  fume: Object.freeze([
    'I have got a long memory and we race again next week.',
    'I do not have anything to say that would print.',
    'He knows what he did and so does everybody watching.',
    'I will handle it on the racetrack where it belongs.',
    'There is a right way to race a man and that was not it.',
    'I have been racing a long time and I do not forget much.',
    'You can take a lot before you stop taking it.',
    'I am going to go home and cool off and then I am still going to be mad.',
    'That is not hard racing. Hard racing I can live with.',
    'I have never wrecked a man on purpose and I am not going to start talking about it now.',
    'We will sort it out. It will not be in an interview.',
    'I would say what I think of it but there are children listening.',
    'Somebody is going to have to answer for that eventually.',
    'I raced him clean all day and I will not make that mistake twice.',
  ]),
  feud: Object.freeze([
    'This is not finished and everybody in the garage knows it.',
    'We will see one another again in a week.',
    'I have run clean with him for a long time and I am done doing that.',
    'It has been coming for a while. Nobody should act surprised.',
    'There is history there and it did not start today.',
    'I do not go looking for it. I do not run from it either.',
    'We will be side by side again and everybody can watch what happens.',
    'You only get pushed so many times.',
    'I have said my piece to him and it was not in front of a camera.',
    'It will keep. Those things always keep.',
    'He can race me however he likes and I will race him the same way back.',
    'I would rather it were over. It is not up to me.',
  ]),
  'top-or-middle-good': Object.freeze([
    'We took what the day gave us and we will take it back next week.',
    'It was not the car we unloaded. It was the one we finished with.',
    'That is a solid day and we needed one.',
    'The crew kept making it better every time I came down pit road.',
    'I would rather run steady every week than good once.',
    'We are closer than we were a month ago and that is the whole game.',
    'You take a clean day and you go home and get ready for the next one.',
    'I never had a car to win with and I had a car to race with.',
    'There is a lot to build on there.',
    'It was not pretty and it was not supposed to be.',
    'Days like this are how you are still around in November.',
  ]),
  'top-or-middle': Object.freeze([
    'Some Sundays you race the racetrack. Today we raced the traffic.',
    'We are a long way from where we want to be.',
    'It got away from us early and we never got it back.',
    'I could not get the front of it to do anything I asked.',
    'We will go home and work and come back and try it again.',
    'It was a long afternoon in there.',
    'Nothing broke, and that is about all I can say for it.',
    'I was tight, then I was loose, then I was just slow.',
    'You cannot pass anything here and we did not have anything to pass with.',
    'We are better than that and we will show it.',
    'I have had worse days. Not many this month.',
    'The crew did their part. I did not do mine.',
  ]),
});

/** How the paper introduces him. */
const ATTRIBUTION: Readonly<Record<InterviewBand, string>> = Object.freeze({
  winner: 'said afterwards',
  wrecked: 'said after climbing out',
  dnf: 'said in the garage',
  fume: 'said afterwards',
  feud: 'said afterwards',
  'top-or-middle': 'said afterwards',
});

/**
 * Which shelf of the bank this man speaks from.
 *
 * ⚠ THE REFINEMENTS READ FACTS, so a first-time winner is only handed the
 * first-win lines when the ledger says it was his first. Guessing would put "you
 * wonder if it is ever going to happen" in the mouth of a man with forty wins.
 */
function bucketFor(interview: PaperInterview, firstCareerWin: boolean): Bucket {
  if (interview.band === 'winner') return firstCareerWin ? 'winner-first' : 'winner';
  if (interview.band === 'top-or-middle') {
    // A good day and a bad one do not sound alike. Tenth is the line a racing
    // man would draw, and it is drawn here rather than left to the prose.
    return interview.finished !== null && interview.finished <= 10 ? 'top-or-middle-good' : 'top-or-middle';
  }
  return interview.band;
}

/**
 * ⚠ DETERMINISTIC, LIKE EVERY OTHER WORD ON THIS PAGE. The same interview gives
 * the same line for ever, so a re-read of race 14 is the edition a player
 * remembers. The WEEK is mixed into the key on purpose: a man interviewed twice
 * in a season says two different things, and without that one driver reads as a
 * recording.
 */
function indexFor(seedText: string, length: number): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seedText.length; i++) h = Math.imul(h ^ seedText.charCodeAt(i), 0x01000193) >>> 0;
  return h % length;
}

/** The quote this edition carries when nobody bought one, or null. */
export function bankQuote(facts: PaperFacts): BankQuote | null {
  const interview = facts.race?.interview ?? null;
  if (!interview) return null;
  const firstWin = interview.band === 'winner' && (facts.race?.winner.firstCareerWin ?? false);
  const bucket = bucketFor(interview, firstWin);
  const lines = BANK[bucket];
  if (!lines || lines.length === 0) return null;
  const key = `${facts.league}|${facts.season}|${facts.racesRun}|${interview.driverId}|${bucket}`;
  return {
    speaker: interview.name,
    text: lines[indexFor(key, lines.length)] as string,
    attribution: ATTRIBUTION[interview.band],
  };
}

/** Every line in the library, for the tests that hold it to its own rules. */
export function everyBankLine(): { bucket: string; text: string }[] {
  return Object.entries(BANK).flatMap(([bucket, lines]) => lines.map((text) => ({ bucket, text })));
}
